import React, { useState, useEffect, Children } from 'react';
import Link from '@mui/material/Link';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import Container from '@mui/material/Container';

import Button from '@mui/material/Button';
import MailOutline from '@mui/icons-material/MailOutline';
import Copyright from '../../components/Copyright';
import { useNotification } from '../../context/NotificationContext';
import {
  WEBSITE_NAME,
  SUPPORT_EMAIL,
  WEBSITE_LINK,
  PRODUCT_NAME,
} from '../../common/AppConstants';

function preventDefault(event) {
  event.preventDefault();
}

const termsList = [
  {
    title: 'Acceptance of Terms',
    content:
      "By accessing or using {WEBSITE_NAME} (the 'Website'), you ('User' or 'you') agree to be bound by these Terms of Service ('Terms'). If you do not agree with these Terms, you may not access or use the Website. Your continued use of the Website following any changes to these Terms constitutes your acceptance of those changes. It is your responsibility to review these Terms periodically for updates.",
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
  {
    title: 'Description of Service',
    content:
      "{WEBSITE_NAME} provides a platform for creating and managing class schedules and timetables for schools and universities (the 'Service'). The Service is provided 'as is' and 'as available', and we make no warranties as to its availability, accuracy, or completeness.",
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
  {
    title: 'Free Access & Future Pricing',
    content:
      '{WEBSITE_NAME} is currently offered as a free service during its initial launch and idea validation phase. Please note that this free access is subject to change.',
    children: [
      {
        content: '',
        listItems: [
          'We reserve the right, at our sole discretion, to introduce paid subscription plans or modify the pricing structure for existing or future features.',
          'If a subscription model is introduced, we will provide reasonable notice to users.',
          'Continued use of the service after the introduction of paid plans may require a subscription and payment of applicable fees.',
          'We do not guarantee that free access will continue indefinitely and reserve the right to terminate free access at any time.',
          'We also reserve the right to modify, suspend, or discontinue the service entirely, with or without notice.',
        ],
      },
    ],
  },
  {
    title: 'Use of Our Service',
    content:
      'Our platform helps you schedule school and university timetables. You must not misuse, copy, reverse engineer, or attempt to hack the system.',
    children: [
      {
        content: 'Misuse includes but is not limited to:',
        listItems: [
          'Using the Service for any illegal or unauthorized purpose.',
          'Impersonating any person or entity, or falsely stating or otherwise misrepresenting your affiliation with a person or entity.',
          'Interfering with or disrupting the Service, servers, or networks connected to the Service.',
          "Attempting to gain unauthorized access to any part of the Service or any other user's account.",
          'Scraping, spidering, crawling, or otherwise extracting data from the Website without our prior written consent.',
          'Reverse engineering, decompiling, or disassembling any part of the Website or the Service for any purpose.',
          'Violating any applicable local, state, national, or international laws.',
        ],
      },
    ],
  },
  {
    title: 'Intellectual Property',
    content:
      'All content on {WEBSITE_NAME}, including but not limited to text, graphics, logos, trademarks, and software, is the property of {WEBSITE_NAME} and is protected by copyright and other intellectual property laws. Unauthorized use, reproduction, or distribution is strictly prohibited. All rights reserved.',
    children: [
      {
        content: 'Users may not:',
        listItems: [
          'Modify, copy, distribute, or exploit any part of our platform without prior written consent.',
          'Claim ownership over any intellectual property belonging to {WEBSITE_NAME}.',
        ],
      },
    ],
  },
  {
    title: 'User Accounts',
    content: '',
    children: [
      {
        content: '',
        listItems: [
          'You are solely responsible for maintaining the security of your account and password.',
          'You must immediately report unauthorized access.',
          'We are not liable for any unauthorized access or actions taken under your account.',
          'We reserve the right to terminate or suspend any account at our sole discretion without liability.',
        ],
      },
    ],
  },
  {
    title: 'Data and Privacy',
    content: '',
    children: [
      {
        content: '',
        listItems: [
          "You represent and warrant that you have all necessary rights, consents, and authorizations to input, access, use, and process any data you provide to the Service, including but not limited to personal data of your organization's employees (including teachers) and students. This includes, but is not limited to, obtaining any required parental consent for student data, complying with all applicable data privacy laws and regulations, and having the authority to bind your organization to these Terms.",
          'You are solely responsible for the accuracy, correctness, and integrity of the data you enter.',
          'While we take reasonable measures to protect your data, we do not guarantee the security of your data.',
          'We do not guarantee the storage or accuracy of your data. You are responsible for backing up your data. We are not liable for any data loss.',
          'We disclaim all liability for data loss, breaches, or corruption, including but not limited to technical failures, cyberattacks, human errors, or external threats.',
          'We reserve the right to delete or modify user data in compliance with applicable laws and our policies.',
          'We do not assume responsibility for data entered by users that violates third-party rights, including data privacy laws.',
          'Our Privacy Policy, available at {WEBSITE_LINK}privacy-policy, explains how we collect, use, and share your data. By using the Service, you agree to the terms of our Privacy Policy.',
        ],
      },
    ],
  },
  {
    title: 'Schedule Data',
    content:
      'Timetables generated by our service are suggestions based on available inputs.',
    children: [
      {
        content: 'We do not guarantee:',
        listItems: [
          'The correctness, compliance, or suitability of schedules.',
          'That generated schedules meet academic, institutional, or regulatory requirements.',
        ],
      },
      {
        content:
          'Users assume full responsibility for verifying and validating all schedules before implementation.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Disclaimer of Warranties',
    content:
      "Our service is provided 'as-is' and 'as-available' without any warranties of any kind, either express or implied.",
    children: [
      {
        content: 'We do not warrant, including but not limited to:',
        listItems: [
          'Merchantability',
          'Fitness for a particular purpose',
          'Non-infringement',
          'Uninterrupted or error-free service',
          'Accuracy, completeness or reliability of any content or information provided through the Service',
          'Correction of any defects or errors.',
        ],
      },
    ],
  },
  {
    title: 'Limitation of Liability',
    content:
      'To the fullest extent permitted by law, we will not be liable for any direct, indirect, incidental, consequential, or punitive damages arising out of or related to your use of the service, even if we have been advised of the possibility of such damages.',
    children: [
      {
        content:
          'Our total liability to you and your organization or its affiliates, shareholders, officers, directors, employees, and agents arising out of or related to these terms or the service will not exceed the amount you paid us for the Service in the 12 months preceding the claim.',
        listItems: [],
      },
      {
        content: 'We are not liable for:',
        listItems: [
          'Scheduling conflicts or incorrect data entries.',
          'The actions or decisions users take based on generated schedules.',
          'Any third-party services or integrations.',
          'Any legal claims not brought within one year of the claim arising.',
        ],
      },
      {
        content: 'You assume all risks associated with using the service.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Indemnification Clause',
    content: '',
    children: [
      {
        content:
          'You agree to indemnify and hold harmless {WEBSITE_NAME}, its affiliates, shareholders, officers, directors, employees, contractors, service providers, and agents from any claims, liabilities, damages, losses, or expenses (including reasonable legal fees) arising out of:',
        listItems: [
          'Your use of the service.',
          'Any violation of these Terms.',
          'Any infringement of third-party rights.',
          'Your failure to comply with applicable laws.',
        ],
      },
    ],
  },
  {
    title: 'Governing Law & Dispute Resolution',
    content:
      'These Terms are governed by the laws of India, without regard to conflict of law principles. Any disputes shall be resolved exclusively in the courts of Delhi, India.',
    children: [
      {
        content: 'By using our service, you agree to:',
        listItems: [
          'Submit to the jurisdiction of these courts.',
          'Waive any objections based on lack of personal jurisdiction or forum non conveniens.',
          'Waive any right to participate in class-action lawsuits against {WEBSITE_NAME}.',
          'All disputes shall first be attempted to be resolved through good-faith negotiations. If unresolved, disputes shall be referred to binding arbitration under the Indian Council of Arbitration.',
        ],
      },
    ],
  },
  {
    title: 'Restricted Access',
    content:
      'Our services are intended for users residing in India. If you access our website from the EU, California (USA), or other regions with specific data protection laws (e.g., GDPR, CCPA), you acknowledge that we do not comply with such regulations.',
    children: [
      {
        content: 'By using our services, you confirm and warrant:',
        listItems: [
          'That you are not subject to such regulations.',
          'That your data will be processed, stored, and governed under the data protection laws of India.',
          'That if you are subject to such regulations, you must not use our services.',
        ],
      },
      {
        content:
          'We disclaim any liability for non-compliance with foreign data protection laws.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Force Majeure',
    content: '',
    children: [
      {
        content:
          'We must not be held liable for any failure to perform due to unforeseen circumstances beyond our control, including but not limited to:',
        listItems: [
          'Natural disasters.',
          'Cyberattacks.',
          'Government actions.',
          'Internet failures.',
          'Strikes or labor disputes.',
        ],
      },
    ],
  },
  {
    title: 'Changes to Terms',
    content:
      'We may update these terms from time to time. Users are responsible for reviewing them regularly. Continued use of the service after changes means you accept them.',
    children: [
      {
        content: 'We reserve the right to:',
        listItems: [
          'Modify or discontinue the service at any time. Significant changes will be notified in advance where feasible.',
          'Suspend or terminate accounts violating these Terms.',
        ],
      },
      {
        content:
          'We will notify users of significant changes via email or website notifications.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Entire Agreement',
    content:
      'These Terms constitute the entire agreement between you and {WEBSITE_NAME} regarding the Service and supersede all prior agreements and understandings.',
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
  {
    title: 'Contact Us',
    content:
      'For any questions or concerns regarding these Terms, please contact us at {SUPPORT_EMAIL}.',
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
];

const replacePlaceholders = (text) => {
  if (!text) return text; // Handle empty strings gracefully
  return text
    .replaceAll('{SUPPORT_EMAIL}', SUPPORT_EMAIL)
    .replaceAll('{WEBSITE_LINK}', WEBSITE_LINK)
    .replaceAll('{WEBSITE_NAME}', WEBSITE_NAME)
    .replaceAll('{PRODUCT_NAME}', PRODUCT_NAME);
};

export default function TermsOfService() {
  const { addNotification } = useNotification();

  return (
    <Container component="main" maxWidth="md" sx={{ py: 5 }}>
      <Typography variant="h3" gutterBottom sx={{ textAlign: 'center', mb: 5 }}>
        Terms of Service
      </Typography>

      {termsList.map((section, section_index) => (
        <Box key={section_index}>
          <Typography variant="h5" sx={{ mt: 3 }}>
            {section_index + 1}
            {'. '}
            {replacePlaceholders(section.title)}
          </Typography>
          <Typography variant="body1">
            {replacePlaceholders(section.content)}
          </Typography>

          {section.children.map((children, children_index) => (
            <Box key={section_index}>
              <Typography variant="body1" sx={{ pt: 1, pb: 0, pl: 0, pr: 0 }}>
                {replacePlaceholders(children.content)}
              </Typography>
              <List disablePadding>
                {children.listItems.map((item, item_index) => (
                  <ListItem disablePadding key={item_index} sx={{ pl: 0 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {'* '}
                          {replacePlaceholders(item)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      ))}

      <Typography variant="h5" sx={{ mt: 3 }}>
        Last Updated: 24th February, 2025
      </Typography>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );
}
